import { keepPreviousData, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import _ from 'lodash'
import { Pencil, Plus } from 'lucide-react'
import { observer } from 'mobx-react'
import { useRef, useState } from 'react'
import { useParams } from 'react-router'

import { DataTable } from '@/components/DataTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { Layout } from '@/components/Layout'
import { getOrganizationAccounts, getOrganizationUsers } from '@/services/organization.service'
import { formatUserName } from '@/utils/userHelpers'

import OrganizationAddUserSlideout from './OrganizationAddUserSlideout'
import OrganizationUserAccessSlideout from './OrganizationUserAccessSlideout'

// Organizations only have Owner/Admin roles — mirrors the backend's
// ORGANIZATION_ROLES choices (rescuex-xverse.api, entities/constants.py),
// which is what the `role` query param validates against.
const ORG_ROLE_OPTIONS = ['Owner', 'Admin']

const OrganizationUsers = observer(() => {
  const { organizationId } = useParams()

  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [facilityFilter, setFacilityFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [pagination, setPagination] = useState({ pageSize: 20, pageIndex: 0 })
  const [editUser, setEditUser] = useState(null)
  const [showAddUser, setShowAddUser] = useState(false)

  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  // Filter options: the org's facilities (same cache key as the access slideout).
  const { data: accounts } = useQuery({
    queryKey: ['organization-accounts', organizationId, 'all'],
    queryFn: () =>
      getOrganizationAccounts({ organizationId, pagination: { pageSize: 1000, pageIndex: 0 } }),
    enabled: !!organizationId,
  })
  const facilityOptions = accounts?.results ?? []

  const filters = {
    ...(facilityFilter ? { facility: facilityFilter } : {}),
    ...(roleFilter ? { role: roleFilter } : {}),
  }

  const { data: users, isPending } = useQuery({
    queryKey: ['organization-users', organizationId, pagination, searchTerm, filters],
    queryFn: () => getOrganizationUsers({ organizationId, pagination, searchTerm, filters }),
    placeholderData: keepPreviousData,
    enabled: !!organizationId,
  })

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  return (
    <Layout breadcrumbs={['Organization', 'Users']}>
      <Layout.Heading title="Users">
        <Button onClick={() => setShowAddUser(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Add User
        </Button>
      </Layout.Heading>
      <p className="text-muted-foreground mb-4 max-w-3xl text-sm">
        Everyone with access to this organization — through an organization role or a role at one
        of its facilities.
      </p>

      <div className="mb-3 flex w-full flex-col items-center gap-4 md:flex-row">
        <Input
          className="w-full md:w-64"
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            debounceSearch(e.target.value)
          }}
        />
        <Select
          value={facilityFilter || 'all'}
          onValueChange={(value) => {
            setFacilityFilter(value === 'all' ? '' : value)
            resetToFirstPage()
          }}
        >
          <SelectTrigger className="w-full md:w-56">
            <SelectValue placeholder="All facilities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All facilities</SelectItem>
            {facilityOptions.map((facility) => (
              <SelectItem key={facility.id} value={facility.id}>
                {facility.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={roleFilter || 'all'}
          onValueChange={(value) => {
            setRoleFilter(value === 'all' ? '' : value)
            resetToFirstPage()
          }}
        >
          <SelectTrigger className="w-full md:w-40">
            <SelectValue placeholder="All org roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All org roles</SelectItem>
            {ORG_ROLE_OPTIONS.map((role) => (
              <SelectItem key={role} value={role}>
                {role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(searchTerm || facilityFilter || roleFilter) && (
          <Button
            variant="outline"
            onClick={() => {
              // A pending debounced call would re-set a stale searchTerm
              // after the clear — cancel it first.
              debounceSearch.cancel()
              setSearch('')
              setSearchTerm('')
              setFacilityFilter('')
              setRoleFilter('')
              resetToFirstPage()
            }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      <DataTable
        columns={[
          {
            id: 'name',
            header: 'Name',
            cell: ({ row }) => (
              <span className={!row.original.lastLogin ? 'text-muted-foreground' : ''}>
                {formatUserName(row.original)}
              </span>
            ),
          },
          {
            accessorKey: 'email',
            header: 'Email',
          },
          {
            id: 'organizationRole',
            header: 'Org Role',
            size: 120,
            cell: ({ row }) => {
              const roles = row.original.organizationRoles ?? []
              return roles.length ? (
                <Badge>{roles.join(', ')}</Badge>
              ) : (
                <span className="text-muted-foreground">—</span>
              )
            },
          },
          {
            id: 'facilities',
            header: 'Facility Access',
            cell: ({ row }) => {
              const facilities = row.original.facilities ?? []
              if (!facilities.length) return <span className="text-muted-foreground">—</span>
              return (
                <div className="flex flex-col gap-0.5">
                  {facilities.map((facility) => (
                    <span key={facility.entityId} className="text-sm">
                      <span className="font-medium">{facility.entityName}</span>
                      <span className="text-muted-foreground"> — {facility.roles.join(', ')}</span>
                    </span>
                  ))}
                </div>
              )
            },
          },
          {
            accessorKey: 'lastLogin',
            header: 'Last Login',
            size: 180,
            cell: ({ row }) =>
              row.original.lastLogin
                ? dayjs(row.original.lastLogin).format('MMM D, YYYY h:mm A')
                : 'N/A',
          },
          {
            id: 'manage',
            size: 150,
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                variant="outline"
                size="sm"
                onClick={() => setEditUser(row.original)}
              >
                <Pencil className="mr-1 h-4 w-4" />
                Manage Access
              </Button>
            ),
          },
        ]}
        data={users?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={users?.count || 0}
        setPagination={setPagination}
      />

      {editUser && (
        <OrganizationUserAccessSlideout
          user={editUser}
          organizationId={organizationId}
          resetAndClose={() => setEditUser(null)}
        />
      )}

      {showAddUser && (
        <OrganizationAddUserSlideout
          organizationId={organizationId}
          resetAndClose={() => setShowAddUser(false)}
        />
      )}
    </Layout>
  )
})

export default OrganizationUsers
